import type { UrlEntity } from '../domain/note'
import type { UnpackedCard } from '../screenshotCleanup/card'

/**
 * One preview for one subject: a note's only link and its unpacked screenshot are the same card when they are about the same thing. The link
 * keeps its layout, picture and actions; the unpacked reading gives it the kind ("Opinion post"), the real text and its own formatting
 * (screenshotCleanup/CleanCard.tsx). Unsure means not merged: both stay, as before.
 */
const SOCIAL_HOSTS = ['x.com', 'twitter.com', 'instagram.com', 'threads.net', 'threads.com', 'tiktok.com', 'facebook.com', 'reddit.com', 'linkedin.com', 'bsky.app']
const MIN_SHARED_WORDS = 2
const MIN_WORD_LENGTH = 4

const host = (address: string) => {
  try { return new URL(address).hostname.replace(/^www\./, '').toLowerCase() } catch { return '' }
}
const words = (text: string) => new Set(text.toLowerCase().normalize('NFKD').replace(/\p{M}/gu, '').split(/[^\p{L}\p{N}]+/u).filter(word => word.length >= MIN_WORD_LENGTH))

export function sameSubject(url: Pick<UrlEntity, 'originalUrl' | 'title' | 'description' | 'author'>, card: UnpackedCard | null | undefined): boolean {
  if (!card || card.plain || card.kind === 'quote' || card.kind === 'other') return false
  // A booking's link is its page (restaurant, airline, venue): there is only one subject in such a note.
  if (card.kind === 'ticket' || card.kind === 'reservation' || card.kind === 'appointment') return true
  const linkHost = host(url.originalUrl)
  if (card.kind === 'post' && SOCIAL_HOSTS.some(social => linkHost === social || linkHost.endsWith(`.${social}`))) return true
  const mine = words([url.title, url.description, url.author].filter(Boolean).join(' '))
  if (!mine.size) return false
  let shared = 0
  for (const word of words(card.words)) if (mine.has(word) && ++shared >= MIN_SHARED_WORDS) return true
  return false
}

/** The words a merged link preview shows as its description: the unpacked reading's, whole, instead of the page's cut-off snippet. */
export function mergedSnippet(card: UnpackedCard): string | undefined {
  const text = [card.kind === 'post' || card.kind === 'article' ? card.title : card.heading ?? card.title, card.excerpt].filter(Boolean).join(' ').trim()
  return text || undefined
}
